import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchServiceDetail } from "../api/SolutionDetailApi";
import ErrorPage from "../not_found/NotFound";
import Loader from "../ui/Loader";
import { useTranslation } from "react-i18next";


const SolutionDetail = () => {
  const { t } = useTranslation("not_found");
  const { id } = useParams();  // Retrieve the ID from the URL
  const [serviceDetail, setServiceDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const getServiceDetail = async () => {
      try {
        setError(null);
        setLoading(true);
        const data = await fetchServiceDetail(id);
        setServiceDetail(data.data);

        console.log("data", data)
      }

      catch (err) {
        setError(<ErrorPage
          title="service_not_found_title"
          description="service_not_found_description"
        />);
      } finally {
        setLoading(false);
      }

    };

    getServiceDetail();
  }, [id]);

  if (loading) return <Loader />
  if (error) return <div className="text-red-500">{error}</div>;

  if (!serviceDetail) return <div>No data available</div>;

  return (
    <div className="relative">
      {/* Hero Section */}
      <div className="relative h-[384px] md:h-[384px] lg:h-[350px]">
        <img
          src="/assets/servicesbg.svg"
          className="w-full h-full object-cover"
          alt="background animation"
        />
        <div className="absolute inset-0 bg-[#00000099]"></div>
        <img
          src="/assets/ellipse.svg"
          className="absolute bottom-64 left-1/2 transform -translate-x-1/2 translate-y-1/2"
          alt="ellipse decoration"
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center px-2 md:px-4">
          <div className="title-small text-[#05897E] py-2 px-4 rounded-full mb-4 border border-[#FFFFFF33]">
          {t("solution")}
          </div>
          <div className="max-w-[1160px]">
            <h1 className="web-heading-main mb-4">{serviceDetail.title}</h1>
            <p className="text-color body-large-30 mb-6 md:text-xl">
              {serviceDetail.short_description}
            </p>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="relative text-white px-4 py-8 max-w-7xl mx-auto mb-10">
        {/* Hero Content */}
        <div className="flex flex-col md:flex-row items-center justify-center">
          <div className="w-full space-y-6">
            {/* Loop over content data */}
            {serviceDetail.content.map((section, index) => {
              if (section.heading1) {
                return (
                  <div key={index}>
                    <h2 className="web-heading-main-4 text-color-6 tracking-tight mb-4">{section.heading1}</h2>
                    {Array.isArray(section.description1) ? (
                      section.description1.map((desc, i) => (
                        <p key={i} className="mb-6 body-large-9 text-color-7 tracking-[1px]">{desc}</p>
                      ))
                    ) : (
                      <p className="mb-6 body-large-9 text-color-7 tracking-[1px]">{section.description1}</p>
                    )}
                    {/* Render features if they exist */}
                    {section.features1 && section.features1.length > 0 && (
                      <ul className="list-disc pl-6 body-large-8 text-color-7 tracking-[1px]">
                        {section.features1.map((feature, i) => (
                          <li key={i}>{feature}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              }
              return null;
            })}

            {/* Tauken AI Solution Section */}
            {serviceDetail.content.map((section, index) => {
              if (section.heading2) {
                return (
                  <div key={index}>
                    <h2 className="web-heading-main-4 text-color-6 tracking-tight mb-4">{section.heading2}</h2>
                    {Array.isArray(section.description2) ? (
                      section.description2.map((desc, i) => (
                        <p key={i} className="mb-6 body-large-9 text-color-7 tracking-[1px]">{desc}</p>
                      ))
                    ) : (
                      <p className="mb-6 body-large-9 text-color-7 tracking-[1px]">{section.description2}</p>
                    )}
                    {/* Render features if they exist */}
                    {section.features2 && section.features2.length > 0 && (
                      <ul className="list-disc pl-6 body-large-8 text-color-7 tracking-[1px]">
                        {section.features2.map((feature, i) => (
                          <li key={i}>{feature}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              }
              return null;
            })}

            {/* Heading 3 Section with Image */}
            {(() => {
              // Get the third content element (id 2, zero-indexed)
              const item = serviceDetail.content[2] || {};

              // Determine if any of the key properties are empty or null.
              // Here we assume that if either description3 or features3 is empty, we use a column layout.
              const isFlexCol =
                (Array.isArray(item.description3) && item.description3.length === 0) ||
                item.description3 === "" ||
                (Array.isArray(item.features3) && item.features3.length === 0) ||
                item.features3 === "";


              const containerClass = isFlexCol ? "flex-col" : "flex-col";

              return (
                <div className={`flex ${containerClass}`}>
                  {serviceDetail.content.map(
                    (section, index) =>
                      section.heading3 && (
                        <div key={index} className="w-full">
                          <h2 className="web-heading-main-4 text-color-6 tracking-tight mb-4">
                            {section.heading3}
                          </h2>
                          {Array.isArray(section.description3) ? (
                            section.description3.map((desc, i) => (
                              <p key={i} className="mb-6 body-large-9 text-color-7 tracking-[1px]">{desc}</p>
                            ))
                          ) : (
                            <p className="mb-6 body-large-9 text-color-7 tracking-[1px]">{section.description3}</p>
                          )}
                          {section.features3 && section.features3.length > 0 && (
                            <ul className="list-disc pl-6 body-large-8 text-color-7 tracking-[1px]">
                              {section.features3.map((feature, i) => (
                                <li key={i}>{feature}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )
                  )}

                  {/* Image Section (Always Rendered) */}
                  <div className="w-full flex justify-center mt-8 relative">
                    <img
                      src={serviceDetail.image}
                      className="max-w-full max-h-auto"
                      alt="Service Image"
                    />
                    <img
                      src="/assets/ellipse.svg"
                      className="absolute inset-0 bottom-8 left-1/2 transform -translate-x-1/2"
                      alt="ellipse decoration"
                    />
                  </div>
                </div>
              );
            })()}





            {/* Heading 4 Section */}

            {serviceDetail.content.map((section, index) => {
              if (section.heading4) {
                return (
                  <div key={index} >
                    <div className="md:w-1/2 space-y-6">
                      <h2 className="web-heading-main-4 text-color-6 tracking-tight mb-4">{section.heading4}</h2>
                      {Array.isArray(section.description4) ? (
                        section.description4.map((desc, i) => (
                          <p key={i} className="mb-6 body-large-9 text-color-7 tracking-[1px]">{desc}</p>
                        ))
                      ) : (
                        <p className="mb-6 body-large-9 text-color-7 tracking-[1px]">{section.description4}</p>
                      )}
                    </div>
                    {/* Render features if they exist */}
                    {section.features4 && section.features4.length > 0 && (
                      <ul className="list-disc list-inside body-large-8 text-color-7 tracking-[1px]">
                        {section.features4.map((feature, i) => (
                          <li key={i}>{feature}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              }
              return null;
            })}


            {/* Heading 5 Section*/}
            {serviceDetail.content.map((section, index) => {
              if (section.heading5) {
                return (
                  <div key={index} className="md:w-1/2 mt-[-229px]">
                    <div className=" pt-3 text-color-7">
                      <h2 className="web-heading-main-5 text-color-6 tracking-tight mb-5 ">{section.heading5}</h2>
                      {Array.isArray(section.description5) ? (
                        section.description5.map((desc, i) => (
                          <p key={i} className="mb-6 body-large-9 text-color-7 tracking-[1px]">{desc}</p>
                        ))
                      ) : (
                        <p className="mb-6 body-large-9 text-color-7 tracking-[1px]">{section.description5}</p>
                      )}
                    </div>


                    {section.features5 && section.features5.length > 0 && (
                      <ul className="list-disc body-large-8 pl-6 body-large-9 text-color-7 tracking-[1px]">
                        {/* <h2 className="web-heading-main-5 text-color-6 tracking-tight mb-2  ">{section.heading5}</h2> */}
                        {section.features5.map((feature, i) => (
                          <li key={i}>{feature}</li>
                        ))}
                      </ul>
                    )}


                  </div>
                );
              }
              return null;
            })}

            {/* Heading 6 Section */}
            {serviceDetail.content.map((section, index) => {
              if (section.heading6) {
                return (
                  <div key={index} className="md:flex md:items-center md:w-1/2 mt-[-150px]">
                    <div className="">
                      {/* <h2 className="web-heading-main-5 text-color-6 tracking-tight mb-2 ">{section.heading6}</h2> */}
                      {Array.isArray(section.description6) ? (
                        section.description6.map((desc, i) => (
                          <p key={i} className="mb-6 body-large-9 text-color-7 tracking-[1px]">{desc}</p>
                        ))
                      ) : (
                        <p className="mb-6 body-large-9 text-color-7 tracking-[1px]">{section.description6}</p>
                      )}
                    </div>

                    {section.features6 && section.features6.length > 0 && (
                      <ul className="list-disc list-inside pl-6 body-large-9 text-color-7 tracking-[1px]">
                        {section.features6.map((feature, i) => (
                          <li key={i}>{feature}</li>
                        ))}
                      </ul>
                    )}

                  </div>
                );
              }
              return null;
            })}
             {/* Dynamic Section 6 onwards*/}

            {serviceDetail.content.map((section, index) => {
              return Object.keys(section).map((key) => {
                const match = key.match(/^heading(\d+)$/);
                if (match) {
                  const number = parseInt(match[1], 10); // Extract the number from "headingX"
                  if (number > 5) { // Only process headings after heading6
                    return (
                      <div key={`${index}-${number}`} className="md:flex flex-col md:items-start md:w-1/2 mt-[-150px]">
                        <div>
                          <h2 className="web-heading-main-5 text-color-6 tracking-tight mb-2">
                            {section[`heading${number}`]}
                          </h2>
                          {Array.isArray(section[`description${number}`]) ? (
                            section[`description${number}`].map((desc, i) => (
                              <p key={i} className="mb-6 body-large-9 text-color-7 tracking-[1px]">{desc}</p>
                            ))
                          ) : (
                            <p className="mb-6 body-large-9 text-color-7 tracking-[1px]">{section[`description${number}`]}</p>
                          )}
                        </div>

                        {section[`features${number}`] && section[`features${number}`].length > 0 && (
                          <ul className="list-disc list-inside pl-6 body-large-9 text-color-7 tracking-[1px]">
                            {section[`features${number}`].map((feature, i) => (
                              <li key={i}>{feature}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  }
                }
                return null;
              });
            })}




          </div>

        </div>
      </div>

    </div>
  );
};

export default SolutionDetail;
